import { type FC, useEffect } from "react";

import { Button, toastError, toastSuccess, useModal } from "@alpacahq/ui/old";

import { useMutationRequest } from "~/v2/api/hooks";
import { deletePaperAccount } from "~/v2/api/rest/account";
import { useAccountContext } from "~/v2/providers/AccountProvider";

export interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentAccountId?: string;
}

export const DeleteAccountModal: FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  currentAccountId,
}) => {
  const {
    currentAccount,
    paperAccounts,
    paperAccountsNoDetail,
    refetchPaperAccounts,
    setCurrentAccount,
  } = useAccountContext();

  // use currentAccount if no id given in props
  const id = currentAccountId ?? currentAccount?.id;

  const accountNumber = paperAccounts.find(
    (account) => account.id === id,
  )?.account_number;

  const deletePaperAccountMutation = useMutationRequest<undefined, undefined>(
    () => deletePaperAccount(id ?? ""),
    {
      onSuccess: () => {
        toastSuccess(
          `You have successfully deleted paper account ${accountNumber}`,
          {},
        );

        // Only move the user off the account they're actively viewing. The
        // deletion target (`id`) may differ from the active account (e.g. from
        // Manage Accounts), in which case the active account must stay put.
        if (currentAccount?.id === id) {
          const nextAccountId = paperAccounts.find((a) => a.id !== id)?.id;
          if (nextAccountId) {
            setCurrentAccount(nextAccountId);
          } else {
            window.location.reload();
          }
        }

        refetchPaperAccounts();
        onClose();
      },
      onError: () => {
        toastError(
          `An error occurred trying to remove paper account ${accountNumber}`,
          {},
        );
      },
    },
  );

  const handleSubmit = () => {
    deletePaperAccountMutation.mutate(undefined);
  };

  const accountName = paperAccountsNoDetail.find(
    (paperAccount) => paperAccount.paper_account_id === id,
  )?.name;

  const {
    component: Modal,
    isOpen: isModalOpen,
    toggle,
  } = useModal({
    title: `Delete Paper Account ${
      accountName ? `${accountName} - ` : ""
    } ${accountNumber}`,
    content: (
      <>
        <div className="mb-2">
          Once you delete this paper account you will no longer have access to
          the data associated with the account.
        </div>

        <div>Are you sure you want to delete this paper account?</div>
      </>
    ),
    controls: (
      <div className="flex space-x-2">
        <Button variant="alternate" onClick={() => toggle()}>
          Close
        </Button>
        <Button onClick={handleSubmit}>Delete</Button>
      </div>
    ),
    onClose: () => {
      if (isModalOpen) {
        onClose();
      }
    },
  });

  useEffect(() => {
    if (isOpen && !isModalOpen) {
      toggle();
    }
  }, [isOpen, isModalOpen, toggle]);

  return <>{Modal}</>;
};
export default DeleteAccountModal;
